// src/components/subscriptions/PaymentReceipt.js
import React from 'react';
import { Logo } from '../../hooks/useLogo';
import './PaymentReceipt.css';
import './SubscriptionInvoice.css';

const PaymentReceipt = ({ payment, onClose }) => {
    const handlePrint = () => {
        const printable = document.querySelector('.receipt-content.inv-wrap');
        if (!printable) {
            window.print();
            return;
        }

        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        if (!printWindow) {
            window.print();
            return;
        }

        // Resolve stylesheet hrefs to absolute URLs - a root-relative href like
        // "/static/css/main.css" can fail to load in a window opened with
        // window.open('', ...) depending on how the browser resolves about:blank's
        // base URL, so make sure every href is unambiguous before copying it over.
        const styleNodes = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
            .map((node) => {
                if (node.tagName === 'LINK') {
                    const clone = node.cloneNode(true);
                    clone.setAttribute('href', new URL(node.getAttribute('href'), window.location.href).href);
                    return clone.outerHTML;
                }
                return node.outerHTML;
            })
            .join('\n');

        printWindow.document.write(`
            <!doctype html>
            <html>
            <head>
                <meta charset="utf-8" />
                <title>Payment Receipt</title>
                <base href="${window.location.origin}/" />
                ${styleNodes}
                <style>
                    body { margin: 0; padding: 16px; background: #fff; }
                    .no-print { display: none !important; }
                </style>
            </head>
            <body>
                ${printable.outerHTML}
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();

        // Wait for the copied stylesheets to actually finish loading instead of
        // guessing with a fixed delay - on a slow connection (especially the
        // external Font Awesome CDN link) a fixed timeout can fire before the
        // styles are applied, which is what makes the print/PDF look unstyled.
        let printed = false;
        const triggerPrint = () => {
            if (printed) return;
            printed = true;
            printWindow.print();
            printWindow.close();
        };
        printWindow.addEventListener('load', () => setTimeout(triggerPrint, 150));
        setTimeout(triggerPrint, 2000); // safety net if 'load' never fires
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            // Some currencies (e.g. PKR) default to 0 fraction digits in Intl's
            // CLDR data even though this app always bills in 2 decimal places -
            // force it explicitly so the figure isn't silently rounded.
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatGateway = (gateway) => {
        if (!gateway) return 'N/A';
        return gateway.charAt(0).toUpperCase() + gateway.slice(1).replace('_', ' ');
    };

    const fmtLong = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const cur = payment.currency || 'USD';
    const statusInfo = (() => {
        const status = (payment.status || '').toLowerCase();
        if (status === 'completed') return { color: '#10b981', text: 'PAID' };
        if (status === 'pending') return { color: '#f59e0b', text: 'PENDING' };
        if (status === 'failed') return { color: '#ef4444', text: 'FAILED' };
        if (status === 'confirming') return { color: '#3b82f6', text: 'CONFIRMING' };
        return { color: '#6b7280', text: (payment.status || 'UNKNOWN').toUpperCase() };
    })();

    return (
        <div className="receipt-modal-overlay" onClick={onClose}>
            <div className="receipt-modal" onClick={(e) => e.stopPropagation()}>
                <button className="receipt-close no-print" onClick={onClose}>
                    <i className="fas fa-times"></i>
                </button>

                <div className="receipt-content inv-wrap">
                    <div className="inv-header">
                        <div className="inv-company">
                            <Logo size="large" className="inv-logo" />
                            <div className="inv-company-details">
                                <span>Attendance Management System</span>
                                <span>register@ehaazri.com</span>
                            </div>
                        </div>
                        <div className="inv-meta-block">
                            <table className="inv-meta-table">
                                <tbody>
                                    <tr><th>Receipt #</th><td>RCPT-{String(payment.id || '').padStart(6, '0')}</td></tr>
                                    <tr><th>Date</th><td>{fmtLong(payment.created_at)}</td></tr>
                                    <tr>
                                        <th>Status</th>
                                        <td>
                                            <span className="inv-status-badge" style={{ background: statusInfo.color }}>
                                                {statusInfo.text}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <hr className="inv-divider" />

                    <div className="inv-billing-row">
                        <div className="inv-bill-section">
                            <div className="inv-section-label">Paid By</div>
                            <div className="inv-org-name">{payment.organization_name || 'Organisation'}</div>
                            <div className="inv-org-id">{payment.organization_email || 'N/A'}</div>
                        </div>
                        <div className="inv-bill-section">
                            <div className="inv-section-label">Payment Details</div>
                            <div className="inv-period-row"><span>Reference:</span> {payment.reference || 'N/A'}</div>
                            <div className="inv-period-row"><span>Method:</span> {formatGateway(payment.gateway_code)}</div>
                            <div className="inv-period-row"><span>Paid On:</span> {formatDate(payment.created_at)}</div>
                        </div>
                    </div>

                    <hr className="inv-divider" />

                    <h2 className="inv-section-heading">Payment Summary</h2>
                    <div className="inv-table-wrap">
                        <table className="inv-table">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th className="inv-th-center">Qty</th>
                                    <th className="inv-th-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        <strong>Haazri License Subscription</strong>
                                        <br />
                                        <small className="receipt-detail-strong">Staff Count: {payment.staff_count || 'N/A'}</small>
                                        {payment.start_date && payment.end_date && (
                                            <>
                                                <br />
                                                <small className="receipt-detail-strong">
                                                    Valid from {new Date(payment.start_date).toLocaleDateString()} to {new Date(payment.end_date).toLocaleDateString()}
                                                </small>
                                            </>
                                        )}
                                    </td>
                                    <td className="inv-td-center">1</td>
                                    <td className="inv-td-right">{formatCurrency(payment.amount, payment.currency)}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr className="inv-total-row">
                                    <td colSpan="2" className="inv-td-label"><strong>Total Paid</strong></td>
                                    <td className="inv-td-right"><strong>{cur} {Number(payment.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {payment.payment_notes && (
                        <div className="inv-notes">
                            <h4>Notes:</h4>
                            <p>{payment.payment_notes}</p>
                        </div>
                    )}

                    <div className="inv-notes">
                        <h4>Please Note:</h4>
                        <ol>
                            <li>This is a computer-generated receipt and does not require a signature.</li>
                            <li>For billing inquiries contact <strong>register@ehaazri.com</strong>.</li>
                        </ol>
                    </div>

                    <div className="inv-print-bar no-print">
                        <button className="btn btn-ghost" onClick={handlePrint}>
                            <i className="fas fa-print"></i>
                            Print Receipt
                        </button>
                        <button className="btn btn-ghost" onClick={onClose}>
                            <i className="fas fa-times"></i>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentReceipt;
